import React from 'react';
import {
  Action, ActionProps, Message, Portal, Stack, Text, useModal,
  useNotifications,
} from '@doveit/bricks';
import { JobApplication } from '../../../providers/api/dtos';
import { updateJobApplication } from '../../../providers/api/job-application/jobApplicationProvider';
import { JobApplicationStatus } from '../../../domain/types';
import SimpleModal from '../../../components/simple-modal/SimpleModal';

interface ChildFnProps {
  loading?: boolean,
  sendOffer: VoidFunction,
}

export interface SendOfferActionProps {
  jobApplication: JobApplication,
  size?: ActionProps['size'],
  onSuccess?: (jobApplication: JobApplication) => void,
  children?: (props: ChildFnProps) => React.ReactNode,
}

export const SEND_OFFER_JOB_APPLICATION_SUCCESS_MESSAGE = 'La candidatura è stata aggiornata correttamente.';
export const SEND_OFFER_JOB_APPLICATION_ERROR_MESSAGE = 'Non è stato possibile aggiornare la candidatura.';

const SendOfferAction: React.FC<SendOfferActionProps> = ({
  jobApplication,
  size = 'M',
  onSuccess,
  children,
}) => {
  const [isSaving, setIsSaving] = React.useState(false);

  const sendOfferModal = useModal();
  const { addSuccess, addError } = useNotifications();

  const openOfferModal = React.useCallback(() => sendOfferModal.open(), [sendOfferModal]);
  const closeOfferModal = React.useCallback(() => sendOfferModal.close(), [sendOfferModal]);

  const sendOffer = React.useCallback(async () => {
    try {
      setIsSaving(true);
      const updatedJobApplication = await updateJobApplication(jobApplication.id!, { ...jobApplication, status: JobApplicationStatus.OFFER });

      onSuccess?.(updatedJobApplication);
      closeOfferModal();
      addSuccess(SEND_OFFER_JOB_APPLICATION_SUCCESS_MESSAGE);
      setIsSaving(false);
    } catch (error) {
      addError(SEND_OFFER_JOB_APPLICATION_ERROR_MESSAGE);
      setIsSaving(false);
    }
  }, [addError, addSuccess, closeOfferModal, jobApplication, onSuccess]);

  return (
    <>
      {
        children
          ? children({ sendOffer: openOfferModal, loading: isSaving })
          : (
            <Action
              label="Invia offerta"
              size={size}
              loading={isSaving}
              onClick={openOfferModal}
              aria-label="Azione per inviare un'offerta al candidato"
            />
          )
      }
      <Portal>
        <SimpleModal
          {...sendOfferModal}
          title="Invia offerta"
          footer={(
            <Action
              label="Invia offerta"
              loading={isSaving}
              onClick={sendOffer}
              color="primary"
              emphasis="high"
              aria-label="Azione per confermare l'invio di un'offerta al candidato"
            />
          )}
          aria-label="Modale per confermare l'invio di un'offerta al candidato"
        >
          <Stack gap={150}>
            <Message
              message="Invio non automatico"
              type="warning"
            />
            <Text.BodySmall>
              L’invio dell’offerta di lavoro non è automatico. Dovrai inviare un’email manualmente con la bozza di contratto in allegato.
            </Text.BodySmall>
          </Stack>
          {/** TODO handle document upload */}
          {/* <Spacing margin={[0, 0, 150]} />
          <DocumentFileManager
            documentId={0}
            documentStatus={DocumentStatus.APPROVED}
            existingFiles={[]}
          /> */}
        </SimpleModal>
      </Portal>
    </>
  );
};

export default SendOfferAction;
